import { useState } from 'react'
import { departmentService } from '../../services/organizationService'
import type { Department, DepartmentCreate, DepartmentUpdate } from '../../types'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'

interface DepartmentFormProps {
  organizationId: string
  department?: Department
  onSuccess?: (dept: Department) => void
  onCancel?: () => void
}

export function DepartmentForm({
  organizationId,
  department,
  onSuccess,
  onCancel,
}: DepartmentFormProps) {
  const [formData, setFormData] = useState<DepartmentCreate>({
    organization_id: organizationId,
    name: department?.name || '',
    description: department?.description || '',
    department_type: department?.department_type || '',
    manager_name: department?.manager_name || '',
    employee_count: department?.employee_count || undefined,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      let result: Department
      if (department) {
        const updateData: DepartmentUpdate = { ...formData }
        delete (updateData as any).organization_id
        result = await departmentService.update(department.id, updateData)
      } else {
        result = await departmentService.create(formData)
      }
      onSuccess?.(result)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save department')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{department ? 'Edit Department' : 'New Department'}</CardTitle>
        <CardDescription>
          {department ? 'Update department details' : 'Create a new department'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Department Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Engineering, Sales, Marketing, etc."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="department_type">Department Type</Label>
            <Input
              id="department_type"
              value={formData.department_type}
              onChange={(e) => setFormData({ ...formData, department_type: e.target.value })}
              placeholder="engineering, sales, marketing, etc."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="manager_name">Manager Name</Label>
            <Input
              id="manager_name"
              value={formData.manager_name}
              onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
              placeholder="John Doe"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="employee_count">Employee Count</Label>
            <Input
              id="employee_count"
              type="number"
              min="0"
              value={formData.employee_count || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  employee_count: e.target.value ? parseInt(e.target.value) : undefined,
                })
              }
              placeholder="25"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the department..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Saving...' : department ? 'Update' : 'Create'}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
